import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Spinner } from '@/components/ui'
import { useAuth } from './useAuth'
import { readStorage, STORAGE_KEYS } from '@/lib/storage'

/** Guards private routes and enforces the legacy profile-completion gate. */
export function RequireAuth() {
  const { user, initializing, isAuthenticated } = useAuth()
  const location = useLocation()

  if (initializing) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spinner label="Loading your workspace…" />
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  const onProfile = location.pathname === '/profile'
  if (!onProfile) {
    const skipProfile = readStorage(STORAGE_KEYS.skipProfileForNow)
    const needsProfile = !!user.is_temporary_password || !user.is_profile_completed || (!skipProfile && user.is_yt_fields_filled === 1)
    if (needsProfile) {
      return <Navigate to="/profile" replace state={{ reason: user.is_temporary_password ? 'temporary-password' : 'incomplete' }} />
    }
  }

  return <Outlet />
}

/**
 * Keeps signed-in users off the auth pages. Honours the `from` location RequireAuth stored, so a
 * login that started from a deep link lands there whether this guard or the login page navigates first.
 */
export function RedirectIfAuthenticated() {
  const { isAuthenticated, initializing } = useAuth()
  const location = useLocation()
  if (initializing) return null
  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />
  }
  return <Outlet />
}
